import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useSurveyDataContext,
  SurveyDataProvider,
} from "./Data/SurveyViewContext";
import AddRecipientsModal from "../../ScheduleV2/AddRecipientsModal";

// Layout
const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #dc3545;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #c82333;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const CardTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DetailItem = styled.div``;

const DetailLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

// Stats
const StatsCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 16px;
  text-align: center;
`;

const StatValue = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 28px;
  color: #476dfa;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
`;

// Table
const TableCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const TableTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const TableHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  width: 240px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const StatusFilter = styled.select`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #fafafa;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  background: ${props => (props.status === 'completed' ? '#e8f5e8' : '#fff4e6')};
  color: ${props => (props.status === 'completed' ? '#2e7d2e' : '#cc8500')};
`;

const EmptyState = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: #7e7e7e;
  font-family: "Poppins";
`;

// Share Access
const AccessCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-top: 24px;
`;

const AccessHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const AccessTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const AccessList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
`;

const AccessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const AccessInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AccessName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const AccessEmail = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #7e7e7e;
`;

const RemoveAccessButton = styled.button`
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  font-family: "Poppins";
  font-size: 12px;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #dc3545;
    background: #fff5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AccessEmptyState = styled.div`
  padding: 24px 20px;
  text-align: center;
  color: #7e7e7e;
  font-family: "Poppins";
  font-size: 13px;
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: 24px 24px 12px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  font-family: "Poppins";
  font-size: 14px;
  color: #414141;
`;

const ShareAccessList = styled.div`
  margin-top: 10px;
  border: 1px solid #eee;
  border-radius: 6px;
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #ffffff;
  scrollbar-width: thin;
  scrollbar-color: rgba(71, 109, 250, 0.35) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(71, 109, 250, 0.35);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const SecondaryButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
`;

// Loading state
const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f0f2f5;
  border-top: 4px solid #476dfa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 16px;
  color: #7e7e7e;
`;

const PulseDataViewContent = ({ surveyId }) => {
  const navigate = useNavigate();
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isAddRecipientsOpen, setIsAddRecipientsOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [shareSearch, setShareSearch] = useState("");
  const [shareSelectedId, setShareSelectedId] = useState(null);
  const [isUpdatingAccess, setIsUpdatingAccess] = useState(false);

  const {
    surveyInfo,
    fetchSurvey,
    surveyParticipants,
    responses,
    hasHydrated,
    saveChangesAndAssignUsers,
    addEmployeeReportAccess,
    get_employees,
    handleCloseSurvey,
    compliance,
    fetched_scheduled_task,
    reportAccess,
    removeEmployeeReportAccess,
    refreshReportAccess,
  } = useSurveyDataContext();

  const accessTaskId =
    surveyId || (fetched_scheduled_task?.id != null ? String(fetched_scheduled_task.id) : "");
  const hasAccessTarget = Boolean(accessTaskId);

  useEffect(() => {
    if (!surveyId) return;
    // Start loading on survey change
    setIsLoading(true);
    setSearchTerm("");
    setStatusFilter("all");
    setShareSearch("");
    setShareSelectedId(null);
    fetchSurvey(surveyId);
    // Scroll to top to reduce perceived flicker
    window.scrollTo({ top: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Mark loaded when the context reports hydration for the current survey
  useEffect(() => {
    if (!surveyId) return;
    const currentId = Number(surveyId);
    const ready = hasHydrated && Number(fetched_scheduled_task?.id) === currentId;
    if (ready) {
      const t = setTimeout(() => setIsLoading(false), 120);
      return () => clearTimeout(t);
    }
  }, [hasHydrated, fetched_scheduled_task?.id, surveyId]);

  const respondedIds = useMemo(
    () => new Set((responses || []).map((r) => Number(r.employee))),
    [responses]
  );

  const enrichedRecipients = useMemo(() => {
    return (surveyParticipants || []).map((p) => ({
      id: p.id,
      name:
        p.employee_full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim(),
      email: p.email,
      status: respondedIds.has(Number(p.id)) ? "completed" : "pending",
    }));
  }, [surveyParticipants, respondedIds]);

  const filteredRecipients = useMemo(() => {
    let list = enrichedRecipients;
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (r) => (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [enrichedRecipients, statusFilter, searchTerm]);

  const existingRecipientIds = useMemo(
    () => (surveyParticipants || []).map((p) => p.id),
    [surveyParticipants]
  );

  const handleAddRecipients = (newIds) => {
    if (!Array.isArray(newIds) || newIds.length === 0) return;
    if (!surveyInfo?.task_template_id) return;
    saveChangesAndAssignUsers(surveyInfo.task_template_id, newIds);
  };

  const shareCandidates = useMemo(() => {
    const all = Array.isArray(get_employees?.employees)
      ? get_employees.employees
      : [];
    const q = shareSearch.toLowerCase();
    return q
      ? all
          .filter(
            (e) =>
              e.status === 1 &&
              (e.first_name?.toLowerCase().includes(q) ||
                e.last_name?.toLowerCase().includes(q) ||
                e.email?.toLowerCase().includes(q))
          )
          .slice(0, 8)
      : [];
  }, [get_employees, shareSearch]);

  const handleShareAccess = async () => {
    if (!shareSelectedId || !hasAccessTarget) return false;
    try {
      setIsUpdatingAccess(true);
      await addEmployeeReportAccess(accessTaskId, [shareSelectedId]);
      refreshReportAccess();
      setShareSearch("");
      setShareSelectedId(null);
      return true;
    } finally {
      setIsUpdatingAccess(false);
    }
  };

  const handleRemoveAccess = async (entry) => {
    const identifier =
      entry?.id != null ? entry.id : entry?.email ? entry.email : null;
    if (!hasAccessTarget || !identifier) return;
    try {
      setIsUpdatingAccess(true);
      await removeEmployeeReportAccess(accessTaskId, [identifier]);
      refreshReportAccess();
    } finally {
      setIsUpdatingAccess(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState>
          <LoadingSpinner />
          <LoadingText>Loading survey…</LoadingText>
        </LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderSection>
        <BackButton onClick={() => navigate("/app/otp/AI/pulse-survey")}>Back</BackButton>
        <HeaderContent>
          <div>
            <Title>{surveyInfo?.title || "Survey"}</Title>
            <Subtitle>Manage survey • {compliance?.percentage || 0}% completion</Subtitle>
          </div>
        </HeaderContent>
        <HeaderActions>
          <PrimaryButton onClick={() => setIsAddRecipientsOpen(true)}>
            Add Recipients
          </PrimaryButton>
          <PrimaryButton
            onClick={() => setIsShareModalOpen(true)}
            disabled={!hasAccessTarget || isUpdatingAccess}
          >
            Share Access
          </PrimaryButton>
          {surveyInfo?.surveyIsOpen && (
            <DangerButton onClick={() => setIsCloseModalOpen(true)}>Close Survey</DangerButton>
          )}
        </HeaderActions>
      </HeaderSection>

      <Grid>
        <Card>
          <CardHeader>
            <CardTitle>Survey Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Created By</DetailLabel>
                <DetailValue>{surveyInfo?.createdBy || ""}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Created Date</DetailLabel>
                <DetailValue>{surveyInfo?.createdDate || ""}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Updated Date</DetailLabel>
                <DetailValue>{surveyInfo?.updatedDate || ""}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Anonymous</DetailLabel>
                <DetailValue>{surveyInfo?.surveyIsAnonymous ? "Yes" : "No"}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </CardContent>
        </Card>

        <StatsCards>
          <StatCard>
            <StatValue>{compliance?.respondedCount || 0}</StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>
              {(compliance?.totalParticipants || 0) - (compliance?.respondedCount || 0)}
            </StatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{compliance?.percentage || 0}%</StatValue>
            <StatLabel>Completion Rate</StatLabel>
          </StatCard>
        </StatsCards>
      </Grid>

      <TableCard>
        <TableHeader>
          <TableTitle>Recipients ({filteredRecipients.length})</TableTitle>
          <TableHeaderActions>
            <StatusFilter value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </StatusFilter>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search recipients..."
            />
          </TableHeaderActions>
        </TableHeader>
        <Table>
          <Thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Status</Th>
            </tr>
          </Thead>
          <tbody>
            {filteredRecipients.map((r) => (
              <tr key={r.id}>
                <Td>{r.name || "N/A"}</Td>
                <Td>{r.email || "N/A"}</Td>
                <Td>
                  <StatusBadge status={r.status}>
                    {r.status === "completed" ? "Completed" : "Pending"}
                  </StatusBadge>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {filteredRecipients.length === 0 && !isLoading && (
          <EmptyState>No recipients match your criteria.</EmptyState>
        )}
      </TableCard>

      <AccessCard>
        <AccessHeader>
          <AccessTitle>Report Access ({reportAccess?.length || 0})</AccessTitle>
          <PrimaryButton
            onClick={() => setIsShareModalOpen(true)}
            disabled={!hasAccessTarget || isUpdatingAccess}
          >
            Share Access
          </PrimaryButton>
        </AccessHeader>
        {reportAccess?.length ? (
          <AccessList>
            {reportAccess.map((entry) => (
              <AccessRow key={entry.id ?? entry.email}>
                <AccessInfo>
                  <AccessName>{entry.name || "Unknown user"}</AccessName>
                  <AccessEmail>
                    {entry.email || "-"}
                    {entry.role ? ` • ${entry.role}` : ""}
                  </AccessEmail>
                </AccessInfo>
                <RemoveAccessButton
                  onClick={() => handleRemoveAccess(entry)}
                  disabled={isUpdatingAccess}
                >
                  Remove
                </RemoveAccessButton>
              </AccessRow>
            ))}
          </AccessList>
        ) : (
          <AccessEmptyState>
            No users currently have report access.
          </AccessEmptyState>
        )}
      </AccessCard>

      {isShareModalOpen && (
        <ModalOverlay onClick={() => setIsShareModalOpen(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Share Access</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <SearchInput
                  value={shareSearch}
                  onChange={(e) => {
                    setShareSearch(e.target.value);
                    setShareSelectedId(null);
                  }}
                  placeholder="Search people by name or email..."
                />
                <PrimaryButton
                  onClick={async () => {
                    const shared = await handleShareAccess();
                    if (shared) setIsShareModalOpen(false);
                  }}
                  disabled={!shareSelectedId || !hasAccessTarget || isUpdatingAccess}
                >
                  Share
                </PrimaryButton>
              </div>
              {shareCandidates.length > 0 && (
                <ShareAccessList>
                  {shareCandidates.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => {
                        setShareSelectedId(c.id);
                        setShareSearch(`${c.first_name} ${c.last_name} • ${c.email}`);
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        background: shareSelectedId === c.id ? "#f0f4ff" : "white",
                      }}
                    >
                      <div style={{ fontFamily: "Poppins", fontSize: 13, color: "#414141" }}>
                        {c.first_name} {c.last_name}
                      </div>
                      <div style={{ fontFamily: "Poppins", fontSize: 11, color: "#7e7e7e" }}>
                        {c.email}
                      </div>
                    </div>
                  ))}
                </ShareAccessList>
              )}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => setIsShareModalOpen(false)}>Cancel</SecondaryButton>
              <PrimaryButton
                onClick={async () => {
                  const shared = await handleShareAccess();
                  if (shared) setIsShareModalOpen(false);
                }}
                disabled={!shareSelectedId || !hasAccessTarget || isUpdatingAccess}
              >
                Share Access
              </PrimaryButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}

      {isAddRecipientsOpen && (
        <AddRecipientsModal
          isOpen={isAddRecipientsOpen}
          onClose={() => setIsAddRecipientsOpen(false)}
          existingRecipients={existingRecipientIds}
          onAddRecipients={handleAddRecipients}
        />
      )}

      {isCloseModalOpen && (
        <ModalOverlay onClick={() => setIsCloseModalOpen(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Close Survey</ModalTitle>
            </ModalHeader>
            <ModalBody>
              Are you sure you want to close this survey? This action cannot be undone.
            </ModalBody>
            <ModalFooter>
              <SecondaryButton onClick={() => setIsCloseModalOpen(false)}>Cancel</SecondaryButton>
              <DangerButton
                onClick={() => {
                  handleCloseSurvey(surveyId);
                  setIsCloseModalOpen(false);
                  navigate("/app/otp/AI/pulse-survey");
                }}
              >
                Close Survey
              </DangerButton>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

const PulseDataViewPage = ({ surveyId }) => {
  const location = useLocation();

  const resolvedSurveyId = useMemo(() => {
    if (surveyId) return surveyId;
    const segments = location.pathname.split("/").filter(Boolean);
    if (!segments.length) return "";
    const lastSegment = segments[segments.length - 1];
    return /^\d+$/.test(lastSegment) ? lastSegment : "";
  }, [surveyId, location.pathname]);

  return (
    <SurveyDataProvider surveyId={resolvedSurveyId}>
      <PulseDataViewContent surveyId={resolvedSurveyId} />
    </SurveyDataProvider>
  );
};

PulseDataViewPage.propTypes = {
  surveyId: PropTypes.string,
};

export default PulseDataViewPage;
